import React, { useState, useEffect, useCallback } from 'react';
import { X, AlertCircle, UserPlus, Mail } from 'lucide-react';
import { toast } from 'sonner';
import useBackdropDismiss from '@/hooks/useBackdropDismiss';
import { inviteUserToTenant } from './api';

/**
 * Phase 1D.8 — Invite User modal, scoped to a specific tenant.
 *
 * Wraps the extended POST /api/admin/invite endpoint. The backend
 * stamps the new user with `tenant_id = tenantId` once we pass
 * `target_tenant_id`. The user receives an invite email via the
 * existing `send_invite_email` flow, clicks the link, lands on the
 * existing /accept-invite page, and sets a password. On their next
 * login they're scoped to this tenant by the JWT.
 *
 * The role picker offers two tenant-scoped roles:
 *   • tenant_admin  — administers a single tenant (AI-employee
 *     assignment delegation + instance provisioning, plus the ARA/ARC
 *     experience and granted-app access).
 *   • tenant_member — a non-admin end-user: uses the tenant's AI
 *     employees (ARA + ARC) with their own session history and views
 *     their own certifications, but has no app access and cannot manage
 *     users or assignments.
 * Both are `is_system` roles owned by SimpliMeta (see routes/roles.py
 * DEFAULT_ROLES). Adding further tiers (e.g. tenant_viewer) is additive —
 * a one-line DEFAULT_ROLE_OPTIONS entry, no behavioural changes elsewhere.
 *
 * Validation:
 *   • email: required, format-validated, not already in use (server)
 *   • name: required
 *   • role: required (only one option in v1, but kept as a select for
 *     forward-compat)
 *
 * Server errors handled inline:
 *   • 400 — "Email already exists" or "archived tenant"
 *   • 403 — permission denied (shouldn't happen for root admin, but
 *     surface it cleanly if it does)
 *   • 404 — tenant no longer exists
 *   • 429 — rate limit (the backend caps at 10/hour on the invite
 *     endpoint via slowapi)
 *   • 500 + "FRONTEND_URL" in detail — friendlier copy explaining
 *     the misconfiguration and that only a SimpliMeta admin can fix
 *     it (added 1D.9)
 *
 * Props
 *   isOpen       boolean
 *   tenantId     string — target tenant for the invite
 *   tenantName   string — for header context
 *   onClose      () => void
 *   onSuccess    (user) => void  — caller reloads page state
 *   roleOptions  [{ value, label, description }] optional (2G.2) — the
 *                roles offered in the picker. Omitted, it falls back to
 *                DEFAULT_ROLE_OPTIONS below, which is the right list for
 *                the root path in TenantDetail: a SimpliMeta admin
 *                provisioning a tenant wants the two platform tenant
 *                roles, not that tenant's private ones.
 *
 *                The tenant-facing Users page passes its own tenant's
 *                roles instead. 2G.1 lets a tenant admin clone a role and
 *                `assignable_role_names` accepts any role the tenant
 *                owns, so without this the clone was authorable and not
 *                assignable — the feature existed with nothing to do.
 *
 *                Presentation only. `assignable_role_names` on the invite
 *                endpoint is the enforcement; a caller passing a role the
 *                server rejects gets the 400, which names what is allowed.
 *
 *   appOptions   [{ app_id, name }] optional (2I.3b) — the apps this
 *                inviter may put in the new user's `app_scope`. Omitted or
 *                empty, the field does not render at all and the invite
 *                behaves exactly as it did before 2I: the server stamps
 *                the inviter's own scope. That is what the root path in
 *                TenantDetail gets, since sidebar-config returns no apps
 *                for a root user and there is nothing honest to offer.
 *
 *                The tenant page sources these from
 *                /api/tenant/sidebar-config, which is already narrowed by
 *                the tenant's grants, the caller's own app_scope, and the
 *                caller's permission keys. So "never wider than your own"
 *                is satisfied by the data source rather than by a check
 *                here — and `_resolve_invitee_scope` enforces it anyway.
 */

// Roles offered in the picker. Keep as an array so adding tenant_member
// or similar in a future phase is a one-line change.
const DEFAULT_ROLE_OPTIONS = [
  {
    value: 'tenant_admin',
    label: 'Tenant Administrator',
    description:
      'Can view users, manage AI Employee assignments and instances within this tenant, and delegate to child tenants.',
  },
  {
    value: 'tenant_member',
    label: 'Tenant Member',
    description:
      'Can use the tenant’s AI Employees (ARA & ARC) with their own chat history and view their own certifications. No app access; cannot manage users or AI Employee assignments.',
  },
];

export default function InviteUserModal({
  isOpen,
  tenantId,
  tenantName,
  onClose,
  onSuccess,
  roleOptions,
  appOptions,
}) {
  // An empty array is treated as absent as well as null/undefined. A
  // picker with nothing in it cannot be submitted, and falling back to
  // the platform roles keeps the invite flow working when the tenant's
  // roles fail to load.
  const options =
    Array.isArray(roleOptions) && roleOptions.length > 0
      ? roleOptions
      : DEFAULT_ROLE_OPTIONS;

  // Same "empty is absent" treatment the role options get above.
  const apps = Array.isArray(appOptions) ? appOptions.filter((a) => a?.app_id) : [];

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState(options[0].value);
  // 2I.3b — app ids ticked for the invitee. EMPTY MEANS INHERIT, not "no
  // apps": the field is omitted from the request entirely, and the server
  // stamps the inviter's own scope. `[]` never means a lockout anywhere in
  // this feature (see services/app_scope), and the UI must not be the one
  // place it does.
  const [appScope, setAppScope] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // `defaultRole` rather than `options` in the deps: `options` is rebuilt
  // every render, so depending on it would reset the form on each keystroke.
  // Keying on the first option's value means the picker re-seeds once, when
  // an async roleOptions fetch resolves after the modal is already open.
  const defaultRole = options[0].value;
  useEffect(() => {
    if (!isOpen) return;
    setEmail('');
    setName('');
    setRole(defaultRole);
    setAppScope([]);
    setErrors({});
    setSubmitting(false);
  }, [isOpen, defaultRole]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose, submitting]);

  const toggleApp = useCallback((appId) => {
    setAppScope((prev) =>
      prev.includes(appId) ? prev.filter((a) => a !== appId) : [...prev, appId],
    );
  }, []);

  const validate = useCallback(() => {
    const errs = {};
    const trimmedEmail = email.trim();
    const trimmedName = name.trim();
    if (!trimmedEmail) {
      errs.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      errs.email = 'Enter a valid email address.';
    }
    if (!trimmedName) errs.name = 'Name is required.';
    if (!role) errs.role = 'Pick a role.';
    return errs;
  }, [email, name, role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSubmitting(true);
    setErrors({});

    try {
      const res = await inviteUserToTenant(tenantId, {
        email: email.trim(),
        name: name.trim(),
        role,
        // Omitted when nothing is ticked. An explicit null would be read
        // by the server as the same "inherit" case, but sending the key
        // at all invites a future reader to make null mean "all apps",
        // which is the one thing a scoped inviter may never grant.
        ...(appScope.length > 0 ? { app_scope: appScope } : {}),
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Invite sent to ${email.trim()}.`);
        onSuccess(data);
        return;
      }

      const errBody = await res.json().catch(() => ({}));
      const detail = errBody?.detail;
      const detailString = typeof detail === 'string' ? detail : detail?.message;

      if (res.status === 400 && detailString?.toLowerCase().includes('email')) {
        setErrors({
          email: detailString || 'Email already in use.',
        });
      } else if (res.status === 400) {
        setErrors({ _global: detailString || 'Invalid request.' });
      } else if (res.status === 403) {
        setErrors({ _global: "You don't have permission to invite users here." });
      } else if (res.status === 404) {
        setErrors({ _global: 'Tenant no longer exists. Refresh the page.' });
      } else if (res.status === 429) {
        setErrors({
          _global: 'Too many invites recently. Wait a few minutes and try again.',
        });
      } else if (
        res.status === 500 &&
        detailString?.toLowerCase().includes('frontend_url')
      ) {
        // 1D.9 — friendlier copy for the FRONTEND_URL misconfiguration.
        // The backend raises this when it can't build the invite link
        // because the env var isn't set. End users can't fix it — only
        // a SimpliMeta admin can. Tell them what's actually wrong and
        // who to contact, instead of the raw "Server configuration
        // error: FRONTEND_URL is not set" string from the backend.
        setErrors({
          _global:
            "The invite link couldn't be generated — this BOSS deployment is missing a config value (FRONTEND_URL). Contact a SimpliMeta admin to fix it; users can't resolve this on their own.",
        });
      } else {
        setErrors({
          _global: detailString || `Request failed (HTTP ${res.status}).`,
        });
      }
    } catch (err) {
      setErrors({ _global: 'Network error. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Backdrop-dismiss guard — extracted to shared hook.
  const backdrop = useBackdropDismiss(onClose, submitting);

  if (!isOpen) return null;

  const selectedRole = options.find((r) => r.value === role);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-modal-title"
      {...backdrop}
    >
      <div
        className="bg-boss-surface border border-boss-border rounded-xl w-full max-w-lg p-6 animate-fadeInUp max-h-[90vh] overflow-y-auto"
        data-testid="invite-user-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-violet-50 dark:bg-violet-600/10 flex items-center justify-center shrink-0">
              <UserPlus size={18} className="text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <h2
                id="invite-modal-title"
                className="text-base font-bold text-boss-text"
              >
                Invite a user to {tenantName}
              </h2>
              <p className="text-xs text-boss-text-secondary mt-1 leading-relaxed">
                The user receives an email with a sign-in link. Once they accept and
                set a password, they can log in and access this tenant's AI Employees.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-boss-text-secondary hover:text-boss-text disabled:opacity-50 ml-2"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Global error */}
        {errors._global && (
          <div className="bg-red-50 dark:bg-red-600/10 border border-red-200 dark:border-red-600/20 rounded-lg p-3 mb-4 flex items-start gap-2">
            <AlertCircle
              size={14}
              className="text-red-600 dark:text-red-400 shrink-0 mt-0.5"
            />
            <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed">
              {errors._global}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label className="text-xs text-boss-text-secondary mb-1 block">
              Email *
            </label>
            <div className="relative">
              <Mail
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-boss-text-muted pointer-events-none"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                className="input-field pl-9"
                placeholder="person@example.com"
                data-testid="invite-email"
                autoFocus
                autoComplete="off"
              />
            </div>
            {errors.email ? (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                {errors.email}
              </p>
            ) : (
              <p className="text-xs text-boss-text-muted mt-1">
                The invite link is sent here. The user sets their own password.
              </p>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="text-xs text-boss-text-secondary mb-1 block">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
              className="input-field"
              placeholder="Full name"
              data-testid="invite-name"
              maxLength={100}
            />
            {errors.name && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                {errors.name}
              </p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="text-xs text-boss-text-secondary mb-1 block">
              Role *
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={submitting}
              className="input-field"
              data-testid="invite-role"
            >
              {options.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            {errors.role ? (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                {errors.role}
              </p>
            ) : selectedRole ? (
              <p className="text-xs text-boss-text-muted mt-1 leading-relaxed">
                {selectedRole.description}
              </p>
            ) : null}
          </div>

          {/* App access (2I.3b) — only when the caller supplies options */}
          {apps.length > 0 && (
            <div>
              <label className="text-xs text-boss-text-secondary mb-1 block">
                App access
              </label>
              <div
                className="space-y-1.5 rounded-lg border border-boss-border p-2.5"
                data-testid="invite-app-scope"
              >
                {apps.map((app) => {
                  const checked = appScope.includes(app.app_id);
                  return (
                    <label
                      key={app.app_id}
                      className="flex items-center gap-2 text-xs text-boss-text-secondary cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={submitting}
                        onChange={() => toggleApp(app.app_id)}
                        data-testid={`invite-app-${app.app_id}`}
                      />
                      <span>{app.name || app.app_id}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-boss-text-muted mt-1 leading-relaxed">
                {appScope.length === 0
                  ? 'Leave all unticked to give this person the same app access you have.'
                  : 'This person will only see the apps ticked above.'}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="btn-secondary disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              data-testid="invite-submit"
            >
              <UserPlus size={14} />
              {submitting ? 'Sending invite…' : 'Send invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
